/* Collectors Guild: the living duo. Planted, not dancing (30 Oct: "alive, like a character").
   Neither character ever bounces, squashes or jumps. Each breathes (a slow swell from the feet), each arm lifts and
   settles on its own rhythm so the two never fall into step, the eyes blink and follow the cursor or the hovered
   button (glancing around by themselves on touch screens), and the body leans a little toward what it watches.
   Dark character (hero): waves on hover, tap or a saved product; five quick taps get a cheer and a star burst.
   Dark character on Add to cart: only watches on hover (the cursor crosses him on the way to the button), waves on
   a tap, and throws his arm up when a product goes in the cart.
   White character (guides): spiral eyes turning about once every 6.7 seconds, whirling on hover or tap while his
   arm waves.
   The arms are separate layers (build_characters.py) turned about their shoulders by SVG attribute; at rest they
   rebuild the artwork exactly.
   ANNIVERSARY (hero, lifted out of the poster by build_shiver.py): every letter shivers on its own 12 fps clock,
   each holding one of four baked micro-poses, re-picked every frame, like a hand-redrawn time-lapse.
   Decorative only (aria-hidden, never focusable). Still under reduced motion; paused off screen and in hidden
   tabs. Review API: window.CollectorsGuildCharacters. */
(() => {
  const SELF = document.currentScript;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const hoverless = matchMedia('(hover: none)');
  const phoneArt = matchMedia('(max-width: 700px)'); // the poster's own artwork switch (<picture> sources and CSS)
  // Taste numbers, live-adjustable from the ?tune panel (guild-editorial-characters-tune.js).
  const tune = {
    rigFps: 60, // the characters' clock: 60 is smooth, 12 is the old stepped cartoon look
    breath: 1.2, // % the body swells on each breath, from the feet
    breathSecs: 3.6,
    sway: 4, // degrees each arm lifts and settles
    swaySecs: 2.6, // per swing; the second arm runs 1.27x slower
    wave: 22, // degrees an arm lifts to wave (a cheer lifts it 1.4x; past about 32 the dark character's shoulder shows)
    lean: 2, // most the body leans toward what it watches, in degrees
    gaze: 1, // eye travel (x)
    blinkMin: 2.5, blinkMax: 6, // seconds between blinks
    spin: .45, // white character: spiral speed (x); locked from Prav's ?tune panel, 30 Oct
    // ANNIVERSARY's shiver, locked from his panel the same day: each pose held two frames (6 changes a second) and a
    // 6 px outline boil.
    fps: 12, shiver: 1.3, shiverTurn: .8, shiverEvery: 2, boil: 6,
  };
  const SPIN_RATE = 120; // spiral degrees a second at spin 1 (one turn every 3 s)
  const BLINK = [.5, .1, .55]; // eye height through a blink, a twelfth of a second each
  const ease = {
    out: x => 1 - (1 - x) ** 3,
    inOut: x => (x < .5 ? 4 * x ** 3 : 1 - (-2 * x + 2) ** 3 / 2),
    pop: x => 1 + 2.2 * (x - 1) ** 3 + 1.2 * (x - 1) ** 2, // a little past the mark, then back
  };
  const clamp01 = x => Math.max(0, Math.min(1, x));
  // Reactions as pure functions of their own time, so any moment can be reviewed (play) or recorded.
  const ACTIONS = {
    wave: {
      ms: 1300, wide: [80, 1000],
      arm: t => {
        const W = tune.wave;
        if (t < 280) return W * ease.out(t / 280);
        if (t < 980) return W + W * .32 * Math.sin(2 * Math.PI * 2 * (t - 280) / 700);
        return W * (1 - ease.inOut(clamp01((t - 980) / 320)));
      },
    },
    cheer: {
      ms: 900, wide: [60, 800], burst: 180,
      arm: t => {
        const W = tune.wave * 1.4;
        if (t < 180) return W * ease.pop(t / 180);
        if (t < 600) return W;
        return W * (1 - ease.inOut(clamp01((t - 600) / 300)));
      },
      swell: t => (t < 180 ? ease.out(t / 180) : t < 600 ? 1 : 1 - ease.inOut(clamp01((t - 600) / 300))),
    },
  };

  function seeded(seed) { return () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; }; }

  class Rig {
    constructor(root) {
      this.root = root;
      this.kind = root.dataset.cgRig;
      this.box = root.closest('.cg-e-character, .cg-e-guide-character') || root;
      this.eyes = this.kind === 'dark' ? [...root.querySelectorAll('.cg-e-rig-eye')] : [];
      this.spirals = [...root.querySelectorAll('.cg-e-rig-spiral')]; this.angle = 0; this.whirl = 0;
      this.arms = [...root.querySelectorAll('.cg-e-rig-arm')].map((g, i) => {
        const [px, py] = g.dataset.pivot.split(' ').map(Number);
        // Raising: a left arm turns clockwise, a right arm counter-clockwise.
        return { g, side: g.dataset.arm, px, py, sign: g.dataset.arm === 'right' ? -1 : 1, period: i ? 1.27 : 1, phase: i ? .37 : 0, angle: 0 };
      });
      this.waver = this.arms.find(arm => arm.side === 'left') || this.arms[0];
      this.buyer = !!root.closest('.cg-e-character--buy'); // beside Add to cart: watches on hover, never waves at it
      this.reveal();
      this.hit = root.querySelector('.cg-e-rig-hit');
      this.target = { x: 0, y: 0 }; this.gaze = { x: 0, y: 0 };
      this.action = null; this.sparks = []; this.taps = []; this.lastWave = -1e9; this.nextGlance = 0;
      this.visible = true; this.t = -1; this.clock = 0; this.running = false; this.focus = null; this.pose = null;
      this.reset();
      this.bind();
    }
    // The body image may be lazy and the arms load as separate files: show the character only once the body and
    // its arms are in, so he never appears armless (arms that fail get 3 s, then he shows anyway).
    reveal() {
      const body = this.root.querySelector('img');
      const settle = node => new Promise(done => { node.addEventListener('load', done, { once: true }); node.addEventListener('error', done, { once: true }); });
      const bodyIn = body && !(body.complete && body.naturalWidth) ? settle(body) : null;
      const armsIn = [...this.root.querySelectorAll('.cg-e-rig-arm image')].map(image => {
        const probe = new Image();
        probe.src = image.getAttribute('href');
        return probe.complete && probe.naturalWidth ? null : settle(probe);
      }).filter(Boolean);
      if (!bodyIn && !armsIn.length) return;
      // opacity, not visibility: a visibility:hidden lazy image is never fetched, so the character would stay hidden.
      this.root.style.opacity = '0';
      const show = () => { this.root.style.opacity = ''; };
      Promise.all([bodyIn, ...armsIn]).then(show);
      setTimeout(show, 3000);
    }
    reset() {
      const random = seeded(29);
      this.blinks = []; let at = 1500;
      for (let i = 0; i < 400; i++) {
        this.blinks.push(at);
        if (random() < .25) this.blinks.push(at + 330);
        at += (tune.blinkMin + random() * (tune.blinkMax - tune.blinkMin)) * 1000;
      }
      // A rare idle tilt, every 8 to 14 s, either way.
      const tilts = seeded(53); this.tilts = []; at = 6000;
      for (let i = 0; i < 200; i++) { this.tilts.push([at, tilts() < .5 ? -1 : 1]); at += 8000 + tilts() * 6000; }
      this.glances = seeded(41);
    }
    bind() {
      if (!this.hit) return;
      if (this.kind !== 'dark') {
        const whirl = force => { if (reduce.matches) return; this.whirl = 6; this.wave(force); this.start(); };
        this.hit.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') whirl(false); });
        this.hit.addEventListener('pointerdown', () => whirl(true));
        return;
      }
      this.hit.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse' && !this.buyer) this.wave(false); });
      this.hit.addEventListener('pointerdown', () => {
        const now = performance.now();
        this.taps = this.taps.filter(time => now - time < 2500).concat(now);
        if (this.taps.length >= 5 && !this.buyer) { this.taps = []; this.trigger('cheer', true); } else this.wave(true);
      });
    }
    wave(force) {
      const now = performance.now();
      if (this.action || (!force && now - this.lastWave < 2500)) return;
      this.trigger('wave');
    }
    trigger(name, burst) {
      if (reduce.matches || !ACTIONS[name] || !this.arms.length) return;
      this.action = { name, start: Math.max(0, this.t), burst: !!burst, burstDone: false };
      if (name === 'wave') this.lastWave = performance.now();
      this.start();
    }
    lookAt(x, y) {
      if (this.kind !== 'dark') return;
      const r = this.root.getBoundingClientRect();
      if (!r.width) return;
      const cx = r.left + r.width * .42, cy = r.top + r.height * .55;
      this.target = { x: Math.max(-1, Math.min(1, (x - cx) / 320)), y: Math.max(-1, Math.min(1, (y - cy) / 320)) };
    }
    // The pose at time t (ms on this character's clock): pure apart from the eased gaze and the spirals.
    compose(t) {
      const s = t / 1000;
      const breath = .5 - .5 * Math.cos(2 * Math.PI * s / tune.breathSecs);
      let swell = 0, wide = false, lift = null;
      const a = this.action;
      if (a) {
        const at = t - a.start, def = ACTIONS[a.name];
        if (at >= def.ms) this.action = null;
        else if (at >= 0) {
          lift = def.arm(at);
          swell = def.swell ? def.swell(at) : 0;
          wide = at >= def.wide[0] && at <= def.wide[1];
          if (a.burst && !a.burstDone && at >= def.burst) { a.burstDone = true; this.burst(t); }
        }
      }
      // Breathing: nearly even, so the body swells rather than squashes; a cheer takes one quick deep breath.
      const grow = tune.breath / 100 * breath + .015 * swell;
      const sy = 1 + grow, sx = 1 + grow * (1 - .3 * (1 - swell));
      const tilt = this.tilts.find(([start]) => t >= start && t < start + 2800);
      const idle = tilt ? tilt[1] * 1.5 * (t - tilt[0] < 800 ? ease.inOut((t - tilt[0]) / 800) : t - tilt[0] < 2000 ? 1 : 1 - ease.inOut((t - tilt[0] - 2000) / 800)) : 0;
      const lean = Math.max(-tune.lean, Math.min(tune.lean, this.gaze.x * tune.lean + idle * (1 - Math.abs(this.gaze.x))));
      const arms = this.arms.map(arm => {
        const sway = tune.sway * (.5 - .5 * Math.cos(2 * Math.PI * (s / (tune.swaySecs * arm.period) + arm.phase)));
        return arm === this.waver && lift !== null ? Math.max(lift, sway * (1 - lift / (tune.wave || 1))) : sway;
      });
      const blinkAt = this.blinks.find(b => t >= b && t < b + BLINK.length * 1000 / 12);
      const blink = !wide && blinkAt !== undefined ? BLINK[Math.floor((t - blinkAt) / (1000 / 12))] : 1;
      return { sx, sy, lean, arms, wide, blink };
    }
    render(ms) {
      const step = 1000 / tune.rigFps, t = Math.floor(ms / step) * step;
      if (t === this.t) return;
      const dt = this.t < 0 ? 0 : Math.max(0, Math.min(t - this.t, 250));
      if (this.kind !== 'dark') {
        // Fresh (seek): the spirals sit where steady turning would have them; live: they advance by the time passed.
        if (this.t < 0) this.angle = (t / 1000 * SPIN_RATE * tune.spin) % 360;
        else {
          this.angle = (this.angle + dt / 1000 * SPIN_RATE * tune.spin * (1 + this.whirl)) % 360;
          this.whirl = this.whirl > .05 ? this.whirl * .82 ** (dt * 12 / 1000) : 0;
        }
      } else {
        if (hoverless.matches && t >= this.nextGlance) { this.target = { x: this.glances() * 2 - 1, y: this.glances() * 1.4 - .5 }; this.nextGlance = t + 3000; }
        const k = 1 - .45 ** (dt * 12 / 1000);
        this.gaze.x += (this.target.x - this.gaze.x) * k; this.gaze.y += (this.target.y - this.gaze.y) * k;
      }
      this.t = t;
      const p = this.pose = this.compose(t);
      this.root.style.transformOrigin = '50% 96%';
      this.root.style.transform = `rotate(${p.lean.toFixed(2)}deg) scale(${p.sx.toFixed(4)},${p.sy.toFixed(4)})`;
      this.arms.forEach((arm, i) => {
        arm.angle = p.arms[i];
        arm.g.setAttribute('transform', `rotate(${(arm.sign * arm.angle).toFixed(2)} ${arm.px} ${arm.py})`);
      });
      for (const spiral of this.spirals) spiral.style.transform = `rotate(${this.angle.toFixed(1)}deg)`;
      const dx = this.gaze.x * 24 * tune.gaze, dy = this.gaze.y * 16 * tune.gaze;
      const ex = p.wide ? 1.08 : 1, ey = (p.wide ? 1.3 : 1) * p.blink;
      for (const eye of this.eyes) eye.style.transform = `translate(${dx.toFixed(1)}px,${(dy - (p.wide ? 8 : 0)).toFixed(1)}px) scale(${ex},${ey})`;
      this.sparks = this.sparks.filter(spark => {
        const k = (t - spark.born) / (1000 / 12);
        if (k > 7) { spark.node.remove(); return false; }
        const d = 18 + k * 13;
        spark.node.style.transform = `translate(${(Math.cos(spark.angle) * d).toFixed(1)}%,${(Math.sin(spark.angle) * d).toFixed(1)}%) scale(${(1 - k / 9).toFixed(2)}) rotate(${(k * 15).toFixed(1)}deg)`;
        return true;
      });
    }
    burst(t) {
      for (let i = 0; i < 7; i++) {
        const node = document.createElement('span');
        node.className = `cg-e-rig-spark${i % 2 ? ' cg-e-rig-spark--blue' : ''}`;
        node.setAttribute('aria-hidden', 'true');
        this.box.append(node);
        this.sparks.push({ node, born: t, angle: (i / 7) * Math.PI * 2 - Math.PI / 2 });
      }
    }
    neutral() {
      this.root.style.transform = ''; this.root.style.transformOrigin = '';
      for (const arm of this.arms) { arm.g.removeAttribute('transform'); arm.angle = 0; }
      for (const eye of this.eyes) eye.style.transform = '';
      for (const spiral of this.spirals) spiral.style.transform = '';
      this.angle = 0; this.whirl = 0; this.pose = null;
      this.sparks.forEach(spark => spark.node.remove()); this.sparks = []; this.action = null; this.t = -1;
    }
    // One loop at a time. A background tab holds its pending frame until you return, so stop() must cancel it: a
    // stale loop running beside the new one fought over the clock and threw the eyes off the character.
    start() {
      if (this.running || reduce.matches || !this.visible || document.hidden) return;
      this.running = true;
      const origin = performance.now() - this.clock;
      const tick = () => {
        if (!this.running) return;
        this.clock = performance.now() - origin;
        this.render(this.clock);
        this.frameId = requestAnimationFrame(tick);
      };
      this.frameId = requestAnimationFrame(tick);
    }
    stop() { this.running = false; if (this.frameId) cancelAnimationFrame(this.frameId); this.frameId = 0; }
  }

  // ANNIVERSARY's letters. Offsets are set in CSS pixels and converted to the word's own units, so the shiver
  // reads the same on the desktop and the phone artwork; only the variant on screen is drawn.
  class Shiver {
    constructor(svg) {
      this.svg = svg; this.kind = 'shiver'; this.phone = svg.classList.contains('cg-e-art-word--phone');
      this.box = svg.closest('.cg-e-poster') || svg;
      this.group = svg.firstElementChild;
      this.letters = [...svg.querySelectorAll('[data-cg-letter]')];
      this.poses = this.letters.map((_, i) => { const r = seeded(101 + i * 13); return [0, 1, 2, 3].map(() => [r() * 2 - 1, r() * 2 - 1, r() * 2 - 1]); });
      this.visible = true; this.frame = -1; this.clock = 0; this.running = false; this.filter = null;
    }
    render(ms) {
      const f = Math.floor(ms / (1000 / tune.fps));
      if (f === this.frame || phoneArt.matches !== this.phone) return;
      this.frame = f;
      const width = this.svg.getBoundingClientRect().width;
      if (!width) return;
      const units = this.svg.viewBox.baseVal.width / width;
      const step = Math.floor(f / Math.max(1, Math.round(tune.shiverEvery)));
      this.letters.forEach((g, i) => {
        const [x, y, turn] = this.poses[i][(step + i) % 4];
        g.setAttribute('transform', `translate(${(x * tune.shiver * units).toFixed(2)} ${(y * tune.shiver * units).toFixed(2)}) rotate(${(turn * tune.shiverTurn).toFixed(2)} ${g.dataset.cx} ${g.dataset.cy})`);
      });
      this.boil(step, units);
    }
    // The outline boil (Prav's fuzzy-text filter turned around: even noise, steady strength, no sideways shake), at the
    // boil dial's value; 0 turns it off.
    boil(step, units) {
      if (!(tune.boil > 0)) { this.group.removeAttribute('filter'); return; }
      if (!this.filter) {
        this.filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
        this.filter.id = `cg-word-boil-${this.phone ? 'phone' : 'desktop'}`;
        for (const [k, v] of Object.entries({ x: '-2%', y: '-2%', width: '104%', height: '104%' })) this.filter.setAttribute(k, v);
        this.filter.innerHTML = '<feTurbulence type="fractalNoise" baseFrequency="0.035" numOctaves="1" seed="1"/><feDisplacementMap in="SourceGraphic" scale="0" xChannelSelector="R" yChannelSelector="G"/>';
        this.svg.prepend(this.filter);
      }
      this.filter.firstElementChild.setAttribute('seed', String(1 + (step % 3)));
      this.filter.lastElementChild.setAttribute('scale', (tune.boil * units).toFixed(2));
      this.group.setAttribute('filter', `url(#${this.filter.id})`);
    }
    neutral() { this.letters.forEach(g => g.removeAttribute('transform')); this.group.removeAttribute('filter'); this.frame = -1; }
  }
  Shiver.prototype.start = Rig.prototype.start;
  Shiver.prototype.stop = Rig.prototype.stop;

  const rigs = [...document.querySelectorAll('[data-cg-rig]')].map(root => new Rig(root));
  const shivers = [...document.querySelectorAll('[data-cg-shiver]')].map(svg => new Shiver(svg));
  const actors = [...rigs, ...shivers];
  if (!actors.length) return;
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      for (const actor of actors.filter(a => a.box === entry.target)) {
        actor.visible = entry.isIntersecting;
        if (actor.visible) actor.start(); else actor.stop();
      }
    }
  }) : null;
  actors.forEach(actor => { if (observer) observer.observe(actor.box); });

  // Watching: the cursor, or the button or link under it.
  document.addEventListener('pointermove', event => {
    if (event.pointerType !== 'mouse') return;
    for (const rig of rigs) {
      if (rig.focus) continue;
      rig.lookAt(event.clientX, event.clientY);
    }
  }, { passive: true });
  document.addEventListener('pointerover', event => {
    const control = event.target.closest?.('a[href], button, summary, [role="button"]');
    if (!control || event.pointerType !== 'mouse') return;
    for (const rig of rigs) {
      if (rig.box.contains(control)) continue;
      const r = control.getBoundingClientRect();
      rig.focus = control; rig.lookAt(r.left + r.width / 2, r.top + r.height / 2);
    }
  });
  document.addEventListener('pointerout', event => {
    for (const rig of rigs) if (rig.focus && !rig.focus.contains(event.relatedTarget)) rig.focus = null;
  });
  // Reacting to the page: a saved product gets a wave from whoever is on screen.
  document.addEventListener('cg:saved', () => rigs.forEach(rig => { if (rig.visible && rig.kind === 'dark') rig.wave(true); }));
  // Add to cart: the character on the buy button throws his arm up the moment it is pressed, so it plays while the
  // add is confirmed; the product form then waits only for what is left of it (at most 650 ms) before the cart page.
  const CHEER_SHOWN = 650;
  let cheerStarted = 0;
  const buyer = () => rigs.find(rig => rig.buyer && rig.visible);
  const cheer = rig => { rig.action = null; rig.trigger('cheer'); cheerStarted = performance.now(); };
  document.addEventListener('submit', event => {
    if (reduce.matches || !event.target.closest?.('product-form')) return;
    const rig = buyer();
    if (rig) cheer(rig);
  }, true);
  document.addEventListener('guild:cart-confirmed', event => {
    if (reduce.matches || !buyer()) return;
    if (!cheerStarted) cheer(buyer());
    const left = Math.max(0, CHEER_SHOWN - (performance.now() - cheerStarted));
    if (event.detail && event.detail.redirect) event.detail.hold = Math.max(event.detail.hold || 0, left);
    cheerStarted = 0;
  });
  document.addEventListener('visibilitychange', () => actors.forEach(actor => (document.hidden ? actor.stop() : actor.start())));
  const motion = () => actors.forEach(actor => { if (reduce.matches) { actor.stop(); actor.neutral(); } else actor.start(); });
  reduce.addEventListener?.('change', motion);
  motion();

  const fresh = rig => { rig.stop(); rig.neutral(); rig.gaze = { x: 0, y: 0 }; rig.target = { x: 0, y: 0 }; };
  window.CollectorsGuildCharacters = {
    version: '2.0.0', tune,
    // Every character and the word as they stand ms into their clocks, from rest (no gaze, no reaction).
    seek(ms) { rigs.forEach(rig => { fresh(rig); rig.clock = ms; rig.render(ms); }); shivers.forEach(s => { s.stop(); s.neutral(); s.clock = ms; s.render(ms); }); return this.snapshot(); },
    // A reaction ms after it starts, on a character otherwise at rest at the start of its clock.
    play(name, ms = 0) { rigs.forEach(rig => { fresh(rig); if (!ACTIONS[name] || !rig.arms.length) return; rig.action = { name, start: 0, burst: false, burstDone: true }; rig.clock = ms; rig.render(ms); }); return this.snapshot(); },
    trigger(name) { rigs.forEach(rig => rig.trigger(name, name === 'cheer')); },
    resume() { rigs.forEach(rig => { rig.reset(); rig.start(); }); shivers.forEach(s => s.start()); },
    retime() { rigs.forEach(rig => rig.reset()); },
    snapshot() {
      return rigs.map(rig => ({
        rig: rig.kind, buyer: rig.buyer, t: rig.t, running: rig.running, visible: rig.visible, action: rig.action?.name || null,
        body: rig.root.style.transform, arms: rig.arms.map(arm => +(arm.sign * arm.angle).toFixed(2)), armTransforms: rig.arms.map(arm => arm.g.getAttribute('transform')),
        pose: rig.pose && { sx: +rig.pose.sx.toFixed(4), sy: +rig.pose.sy.toFixed(4), lean: +rig.pose.lean.toFixed(2) },
        eyes: rig.eyes.map(e => e.style.transform), spirals: rig.spirals.map(s => s.style.transform), whirl: +rig.whirl.toFixed(2), sparks: rig.sparks.length,
      }));
    },
    shiverSnapshot() { return shivers.map(s => ({ variant: s.phone ? 'phone' : 'desktop', letters: s.letters.length, frame: s.frame, running: s.running, visible: s.visible, drawn: !!s.svg.getBoundingClientRect().width, boil: s.group.hasAttribute('filter'), transforms: s.letters.map(g => g.getAttribute('transform')) })); },
    whirl() { rigs.forEach(rig => { if (rig.kind !== 'dark' && !reduce.matches) { rig.whirl = 6; rig.wave(true); rig.start(); } }); },
  };
  // The slider panel loads only with ?tune in the address: no production footprint.
  if (/[?&]tune\b/.test(location.search) && SELF?.src) {
    const panel = document.createElement('script');
    panel.src = SELF.src.replace('guild-editorial-characters.js', 'guild-editorial-characters-tune.js');
    document.head.append(panel);
  }
})();
